export interface QueuedFile {
  id: string;
  file: File;
  status: 'queued' | 'uploading' | 'processing' | 'completed' | 'failed';
  progress: number;
  s3Key?: string;
  reportId?: string;
  error?: string;
  startTime: Date;
  endTime?: Date;
  retryCount: number;
  uploadUrl?: string;
  metadata?: {
    user_email: string;
    original_filename: string;
    upload_timestamp: string;
  };
  // For PDFs converted to PNGs
  isPdfConverted?: boolean;
  pngFiles?: File[];
  currentPage?: number;
  totalPages?: number;
}

export interface UploadCallbacks {
  onProgress?: (fileId: string, progress: number) => void;
  onStatusChange?: (fileId: string, status: QueuedFile['status'], error?: string) => void;
  onComplete?: (fileId: string, reportId?: string) => void;
  onError?: (fileId: string, error: string) => void;
  onQueueChange?: (queue: QueuedFile[]) => void;
}

export class UploadQueueManager {
  private queue: QueuedFile[] = [];
  private processing: Map<string, QueuedFile> = new Map();
  private allFiles: Map<string, QueuedFile> = new Map(); // Track all files including those polling
  private emlAttachmentMap: Map<string, string[]> = new Map(); // EML file ID -> attachment file IDs
  private attachmentParentMap: Map<string, string> = new Map(); // Attachment file ID -> parent EML file ID
  private readonly maxConcurrent = 5; // Concurrent processing limit
  private readonly maxRetries = 3;
  private callbacks: UploadCallbacks = {};
  private apiEndpoint: string;
  private userEmail: string;
  private getAuthToken?: () => Promise<string | undefined>;
  private getIdTokenClaims?: () => Promise<any>;

  constructor(apiEndpoint: string, userEmail: string, callbacks?: UploadCallbacks, getAuthToken?: () => Promise<string | undefined>, getIdTokenClaims?: () => Promise<any>) {
    this.apiEndpoint = apiEndpoint;
    this.userEmail = userEmail;
    this.callbacks = callbacks || {};
    this.getAuthToken = getAuthToken;
    this.getIdTokenClaims = getIdTokenClaims;
  }

  public async addFiles(files: FileList | File[]): Promise<void> {
    const fileArray = Array.from(files);

    // Validate files
    const validFiles = fileArray.filter(file => this.validateFile(file));

    // Create queue entries - PDFs stay as single entry but with PNG pages internally
    const newQueuedFiles: QueuedFile[] = [];

    for (const file of validFiles) {
      const queuedFile: QueuedFile = {
        id: this.generateFileId(),
        file,
        status: 'queued' as const,
        progress: 0,
        startTime: new Date(),
        retryCount: 0
      };

      if (file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')) {
        // Convert PDF to PNG images but keep as single queue entry
        try {
          const { convertPdfToImages, isPdfFile } = await import('./pdfToImageConverter');
          console.log(`Converting PDF ${file.name} to PNG images...`);

          // Update status to show conversion happening
          queuedFile.status = 'uploading';
          this.callbacks.onStatusChange?.(queuedFile.id, 'uploading');

          // Convert the PDF to images
          const convertedImages = await convertPdfToImages(file, this.getIdTokenClaims!);

          // Async conversion: the converter already dropped the real pages into the
          // pipeline, so there are no page files to create/upload here (the marker
          // blob would otherwise become a junk 5-byte file). Mark completed.
          if (convertedImages.length === 1 && (convertedImages[0] as any).asyncProcessing) {
            queuedFile.isPdfConverted = true;
            queuedFile.pngFiles = [];
            queuedFile.totalPages = 0;
            queuedFile.currentPage = 0;
            queuedFile.status = 'completed';
            this.callbacks.onStatusChange?.(queuedFile.id, 'completed');
            console.log(`[UploadQueue] ${(convertedImages[0] as any).message || 'Large PDF processing in background'}`);
          } else {
            // Create File objects from the blobs
            const pngFiles: File[] = [];
            for (const imageData of convertedImages) {
              const imageFile = new File(
                [imageData.blob],
                imageData.fileName,
                { type: 'image/png' }
              );
              pngFiles.push(imageFile);
              console.log(`Converted page ${imageData.pageNumber} of ${file.name}`);
            }

            // Store PNG files in the queued file object
            queuedFile.isPdfConverted = true;
            queuedFile.pngFiles = pngFiles;
            queuedFile.totalPages = pngFiles.length;
            queuedFile.currentPage = 0;
            queuedFile.status = 'queued';

            console.log(`Successfully converted ${file.name} to ${pngFiles.length} PNG images`);
          }
        } catch (error) {
          console.error(`Failed to convert PDF ${file.name}:`, error);
          queuedFile.status = 'failed';
          queuedFile.error = `Failed to convert PDF: ${error}`;
          this.callbacks.onError?.(queuedFile.id, queuedFile.error);
        }
      }

      newQueuedFiles.push(queuedFile);
    }

    // Set up EML tracking for files with attachments
    newQueuedFiles.forEach(queuedFile => {
      if (queuedFile.file.name.toLowerCase().endsWith('.eml')) {
        // For now, we'll set up the mapping when we know the attachment files
        // This will be populated when the backend processes the EML
        this.emlAttachmentMap.set(queuedFile.id, []);
        console.log(`Added EML to tracking map: ${queuedFile.file.name} (ID: ${queuedFile.id})`);
      }
    });

    // Add to queue and track all files
    this.queue.push(...newQueuedFiles);
    newQueuedFiles.forEach(f => this.allFiles.set(f.id, f));

    // Notify queue change
    this.callbacks.onQueueChange?.(this.getAllFiles());

    // Start processing
    this.processNext();
  }

  private validateFile(file: File): boolean {
    const allowedExtensions = ['.pdf', '.png', '.jpg', '.jpeg', '.xlsx', '.xls', '.eml', '.txt', '.csv'];
    const maxSize = 50 * 1024 * 1024; // 50MB

    const extension = '.' + file.name.split('.').pop()?.toLowerCase();

    if (!allowedExtensions.includes(extension)) {
      this.callbacks.onError?.(file.name, `Invalid file type. Allowed types: ${allowedExtensions.join(', ')}`);
      return false;
    }

    if (file.size > maxSize) {
      this.callbacks.onError?.(file.name, `File too large. Maximum size is 50MB.`);
      return false;
    }

    return true;
  }

  private async processNext(): Promise<void> {
    // Check if we can process more files
    if (this.processing.size >= this.maxConcurrent || this.queue.length === 0) {
      return;
    }

    // Get next file from queue
    const file = this.queue.shift();
    if (!file) return;

    // Add to processing
    this.processing.set(file.id, file);

    // Start processing
    try {
      await this.processFile(file);
    } catch (error) {
      console.error(`Error processing file ${file.id}:`, error);
      // Only remove from processing if there was an error during upload
      this.processing.delete(file.id);
    }

    // Process next file immediately (file is still processing in background)
    this.processNext();
  }

  private async processFile(queuedFile: QueuedFile): Promise<void> {
    try {
      // Update status to uploading
      this.updateFileStatus(queuedFile, 'uploading');

      // Handle PDFs that have been converted to PNGs
      if (queuedFile.isPdfConverted && queuedFile.pngFiles) {
        const PAGE_DELAY = 1000; // 1 second delay between each page to avoid rate limiting

        // Upload pages sequentially with delay
        for (let i = 0; i < queuedFile.pngFiles.length; i++) {
          const pngFile = queuedFile.pngFiles[i];
          const pageNumber = i + 1;
          queuedFile.currentPage = pageNumber;

          try {
            // Get presigned URL for this PNG
            const presignedData = await this.getPresignedUrl(pngFile);

            // Upload the PNG page
            await this.uploadFileToS3(pngFile, presignedData.uploadUrl, presignedData.requiredMetadata);

            console.log(`Uploaded page ${pageNumber}/${queuedFile.pngFiles.length} of ${queuedFile.file.name}`);

            // Update progress
            const progressPercent = Math.round((pageNumber / queuedFile.pngFiles.length) * 100);
            this.callbacks.onProgress?.(queuedFile.id, progressPercent);

            // Add delay between pages (except for the last page)
            if (i < queuedFile.pngFiles.length - 1) {
              await new Promise(resolve => setTimeout(resolve, PAGE_DELAY));
            }
          } catch (error) {
            console.error(`Failed to upload page ${pageNumber}:`, error);
            throw error; // Let the retry logic handle it
          }
        }

        // Use the last s3Key for tracking
        queuedFile.s3Key = queuedFile.file.name.replace('.pdf', '_page_1.png').replace('.PDF', '_page_1.png');
      } else {
        // Regular file upload (non-PDF)
        const presignedData = await this.getPresignedUrl(queuedFile.file);
        queuedFile.uploadUrl = presignedData.uploadUrl;
        queuedFile.s3Key = presignedData.s3Key;
        queuedFile.metadata = presignedData.requiredMetadata;

        await this.uploadToS3(queuedFile);
      }

      // Step 3: Update status to processing
      this.updateFileStatus(queuedFile, 'processing');

      // Step 4: Keep file in processing map until FULLY complete
      // DO NOT remove from processing map here - wait until fully processed

      // Step 5: Start polling and wait for completion before processing next
      this.startBackgroundPolling(queuedFile).then(() => {
        // File is fully processed, now remove from processing map
        this.processing.delete(queuedFile.id);
        // NOW we can process the next file
        this.processNext();
      }).catch((error) => {
        console.error(`Background polling failed for ${queuedFile.id}:`, error);
        this.handleFileError(queuedFile, error);
        // Remove from processing on error too
        this.processing.delete(queuedFile.id);
        this.processNext();
      });

    } catch (error: any) {
      await this.handleFileError(queuedFile, error);
    }
  }

  private async startBackgroundPolling(queuedFile: QueuedFile): Promise<void> {
    try {
      // Poll for processing status
      const reportId = await this.pollForCompletion(queuedFile);
      queuedFile.reportId = reportId;

      // Mark as completed even if reportId is undefined (timeout case)
      // File might be in DB but status endpoint is slow
      this.updateFileStatus(queuedFile, 'completed');
      queuedFile.endTime = new Date();

      this.callbacks.onComplete?.(queuedFile.id, reportId);
    } catch (error: any) {
      console.error(`Polling failed for ${queuedFile.file.name}:`, error);
      // Mark as completed with warning - file might be in DB
      this.updateFileStatus(queuedFile, 'completed');
      queuedFile.endTime = new Date();
      queuedFile.error = 'Processing may have completed - check upload history';
      this.callbacks.onComplete?.(queuedFile.id, undefined);
    }
  }

  private async handleFileError(queuedFile: QueuedFile, error: any): Promise<void> {
    // Handle retry logic
    if (queuedFile.retryCount < this.maxRetries) {
      queuedFile.retryCount++;
      console.log(`Retrying file ${queuedFile.id}, attempt ${queuedFile.retryCount}`);

      // Add delay before retry to avoid rate limiting
      const retryDelay = Math.min(1000 * Math.pow(2, queuedFile.retryCount), 5000); // Exponential backoff, max 5 seconds
      await new Promise(resolve => setTimeout(resolve, retryDelay));

      // Re-add to queue for retry
      queuedFile.status = 'queued';
      this.queue.unshift(queuedFile); // Add to front of queue
      this.processNext();
    } else {
      // Max retries exceeded
      this.updateFileStatus(queuedFile, 'failed', error.message || 'Upload failed');
      queuedFile.endTime = new Date();
      this.callbacks.onError?.(queuedFile.id, error.message);
    }
  }

  private async getPresignedUrl(file: File): Promise<any> {
    // Ensure proper MIME type for text files
    let contentType = file.type || 'application/octet-stream';
    if (file.name.toLowerCase().endsWith('.txt')) {
      contentType = 'text/plain';
    } else if (file.name.toLowerCase().endsWith('.csv')) {
      contentType = 'text/csv';
    }

    console.log(`Requesting presigned URL for file: ${file.name}, type: ${contentType}, user: ${this.userEmail}`);

    // Get Auth0 token if available
    const authToken = this.getAuthToken ? await this.getAuthToken() : undefined;
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
    }

    const response = await fetch(`${this.apiEndpoint}/upload-url`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        filename: file.name,
        contentType: contentType
      })
    });

    if (!response.ok) {
      const error = await response.json();
      console.error(`Presigned URL request failed: ${response.status}`, error);
      throw new Error(error.error || 'Failed to get upload URL');
    }

    return response.json();
  }

  private async uploadFileToS3(file: File, uploadUrl: string, metadata: any): Promise<void> {
    // Helper method for uploading a single file to S3
    let contentType = file.type || 'application/octet-stream';
    if (file.name.toLowerCase().endsWith('.txt')) {
      contentType = 'text/plain';
    } else if (file.name.toLowerCase().endsWith('.csv')) {
      contentType = 'text/csv';
    }

    const headers: HeadersInit = {
      'Content-Type': contentType,
      'x-amz-meta-user_email': metadata.user_email,
      'x-amz-meta-original_filename': metadata.original_filename,
      'x-amz-meta-upload_timestamp': metadata.upload_timestamp
    };

    const response = await fetch(uploadUrl, {
      method: 'PUT',
      headers,
      body: file
    });

    if (!response.ok) {
      throw new Error(`Upload failed with status ${response.status}`);
    }
  }

  private async uploadToS3(queuedFile: QueuedFile): Promise<void> {
    if (!queuedFile.uploadUrl || !queuedFile.metadata) {
      throw new Error('Missing upload URL or metadata');
    }

    // Ensure proper MIME type for text files
    let contentType = queuedFile.file.type || 'application/octet-stream';
    if (queuedFile.file.name.toLowerCase().endsWith('.txt')) {
      contentType = 'text/plain';
    } else if (queuedFile.file.name.toLowerCase().endsWith('.csv')) {
      contentType = 'text/csv';
    }

    // Create headers with S3 metadata
    const headers: HeadersInit = {
      'Content-Type': contentType,
      'x-amz-meta-user_email': queuedFile.metadata.user_email,
      'x-amz-meta-original_filename': queuedFile.metadata.original_filename,
      'x-amz-meta-upload_timestamp': queuedFile.metadata.upload_timestamp
    };

    // Use XMLHttpRequest for progress tracking
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const progress = Math.round((event.loaded / event.total) * 100);
          this.callbacks.onProgress?.(queuedFile.id, progress);
          queuedFile.progress = progress;
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status === 200 || xhr.status === 204) {
          resolve();
        } else {
          reject(new Error(`Upload failed with status ${xhr.status}`));
        }
      });

      xhr.addEventListener('error', () => {
        reject(new Error('Network error during upload'));
      });

      xhr.open('PUT', queuedFile.uploadUrl!);

      // Set headers
      Object.entries(headers).forEach(([key, value]) => {
        xhr.setRequestHeader(key, value as string);
      });

      xhr.send(queuedFile.file);
    });
  }

  private async pollForCompletion(queuedFile: QueuedFile): Promise<string | undefined> {
    const maxPolls = 150; // Poll for up to 5 minutes (increased from 2)
    const pollInterval = 2000; // 2 seconds

    for (let i = 0; i < maxPolls; i++) {
      await new Promise(resolve => setTimeout(resolve, pollInterval));

      // Get Auth0 token if available
      const authToken = this.getAuthToken ? await this.getAuthToken() : undefined;
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
      }

      const response = await fetch(`${this.apiEndpoint}/status/${queuedFile.s3Key}`, {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        console.warn(`Status check failed for ${queuedFile.s3Key}`);
        continue;
      }

      const status = await response.json();

      if (status.status === 'processed') {
        // Log multi-page PDF completion
        if (status.pageCount && status.pageCount > 1) {
          console.log(`Multi-page PDF complete: ${queuedFile.file.name} (${status.pageCount} pages)`);
        }

        // For EML files, reportId might be null since they don't create DB entries
        // For multi-page PDFs, reportId will be the first page's ID
        // Return reportId if available, otherwise return a placeholder
        return status.reportId || 'eml_processed';
      } else if (status.status === 'failed') {
        throw new Error('Processing failed');
      }
      // Continue polling if still processing
    }

    // After timeout, check one more time if it exists in DB
    console.warn(`Processing timeout for ${queuedFile.file.name} - checking if already in DB`);

    // Mark as completed anyway since it might be in DB but status endpoint is slow
    // The upload history will show it if it's in the database
    return undefined;
  }

  private updateFileStatus(file: QueuedFile, status: QueuedFile['status'], error?: string): void {
    const oldStatus = file.status;
    file.status = status;
    if (error) {
      file.error = error;
    }

    // Log status changes for EML files
    if (file.file.name.toLowerCase().endsWith('.eml')) {
      console.log(`EML status change: ${file.file.name} ${oldStatus} → ${status}`);
    }

    // Update in our tracking map
    this.allFiles.set(file.id, file);

    this.callbacks.onStatusChange?.(file.id, status, error);
    this.callbacks.onQueueChange?.(this.getAllFiles());
  }

  // Method to check if EML attachments have completed and mark parent EML as complete
  public checkEmlAttachmentCompletion(uploadHistory: any[]): void {
    console.log(`checkEmlAttachmentCompletion called with ${uploadHistory.length} history items`);
    console.log(`EML attachment map size: ${this.emlAttachmentMap.size}`);
    console.log('EML attachment map:', Array.from(this.emlAttachmentMap.entries()));

    // Go through each EML file that's still processing
    this.emlAttachmentMap.forEach((attachmentIds, emlFileId) => {
      const emlFile = this.allFiles.get(emlFileId);

      if (emlFile && emlFile.status === 'processing') {
        // Check if all attachments for this EML have completed
        // Look for attachments in upload history that have s3_key indicating they came from this EML
        const emlFileName = emlFile.file.name;
        const emlBaseName = emlFileName.replace('.eml', '');

        // EML attachments have s3_key pattern: "attachments/incoming/{timestamp-emlname}/{attachment}"
        // We need to find records where s3_key contains the EML filename
        const relatedAttachments = uploadHistory.filter(item => {
          if (!item.s3_key) return false;

          // Check if s3_key contains path indicating it's an attachment from this EML
          // Pattern: attachments/incoming/*{emlFileName}/*

          // More flexible matching - check if s3_key contains the EML base name
          const isAttachmentPath = item.s3_key.includes('attachments/incoming/') &&
                                  item.s3_key.split('/').length >= 4;

          if (!isAttachmentPath) return false;

          // Extract the EML folder name from the path: attachments/incoming/FOLDER/file
          const pathParts = item.s3_key.split('/');
          const emlFolder = pathParts[2]; // attachments/incoming/[FOLDER]/file

          // Check if the EML folder contains the base name (more flexible matching)
          return emlFolder.includes(emlBaseName);
        });

        console.log(`EML ${emlFileName}: Found ${relatedAttachments.length} related attachments`);
        console.log(`EML base name: "${emlBaseName}"`);
        console.log('All attachment paths:', uploadHistory.filter(item => item.s3_key?.includes('attachments/incoming/')).map(item => item.s3_key));
        if (relatedAttachments.length > 0) {
          console.log('Matched attachments:', relatedAttachments.map(a => ({ s3_key: a.s3_key, status: a.status, confidence: a.confidence })));
        }

        // If we have attachments and they're all completed
        if (relatedAttachments.length > 0) {
          const allCompleted = relatedAttachments.every(attachment =>
            attachment.status === 'completed' ||
            attachment.status === 'review' ||
            (attachment.confidence !== undefined && attachment.confidence > 0)
          );

          if (allCompleted) {
            console.log(`All attachments completed for EML: ${emlFileName}, marking as completed`);
            this.updateFileStatus(emlFile, 'completed');
            emlFile.endTime = new Date();
            this.callbacks.onComplete?.(emlFile.id, 'eml_completed');
          } else {
            console.log(`EML ${emlFileName}: Some attachments still processing`, relatedAttachments.map(a => ({s3_key: a.s3_key, status: a.status})));
          }
        }
      }
    });
  }

  private generateFileId(): string {
    return `file-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  public getAllFiles(): QueuedFile[] {
    // Return all files from our tracking map
    return Array.from(this.allFiles.values());
  }

  public getQueueLength(): number {
    return this.queue.length;
  }

  public getProcessingCount(): number {
    return this.processing.size;
  }

  public cancelFile(fileId: string): void {
    // Get file from tracking
    const file = this.allFiles.get(fileId);
    if (!file) return;

    // Remove from queue
    this.queue = this.queue.filter(f => f.id !== fileId);

    // If processing, mark as failed
    if (this.processing.has(fileId)) {
      this.processing.delete(fileId);
    }

    // Update status and remove from tracking
    this.updateFileStatus(file, 'failed', 'Cancelled by user');

    this.callbacks.onQueueChange?.(this.getAllFiles());
  }

  public retryFile(fileId: string): void {
    const allFiles = this.getAllFiles();
    const file = allFiles.find(f => f.id === fileId);

    if (file && file.status === 'failed') {
      file.status = 'queued';
      file.retryCount = 0;
      file.error = undefined;
      this.queue.push(file);
      this.processNext();
    }
  }

  public clearCompleted(): void {
    // Remove completed files from tracking
    const completedIds = Array.from(this.allFiles.values())
      .filter(f => f.status === 'completed')
      .map(f => f.id);

    completedIds.forEach(id => {
      this.allFiles.delete(id);
      this.processing.delete(id);
    });

    // Remove from queue if there
    this.queue = this.queue.filter(f => !completedIds.includes(f.id));

    // Notify change
    this.callbacks.onQueueChange?.(this.getAllFiles());
  }
}